import { motion, useReducedMotion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

/**
 * Scroll-reveal wrapper — fades/slides content in once when it enters the viewport.
 * Falls back to a plain div when the user prefers reduced motion.
 */
export function Reveal({ children, delay = 0, y = 28, className, ...rest }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: "-50px" }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Kinetic masked word-by-word reveal for the hero headline.
 * Works with dynamic CMS strings of any length: each word slides up
 * out of an overflow-hidden mask with a stagger.
 */
export function MaskedWords({ text, baseDelay = 0.15, stagger = 0.06 }) {
  const reduce = useReducedMotion();
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (reduce) return <>{text}</>;
  return (
    <span aria-label={text} className="inline">
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          aria-hidden="true"
          className="inline-block overflow-hidden align-bottom pb-[0.1em] -mb-[0.1em]"
        >
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.85, delay: baseDelay + i * stagger, ease: [0.16, 1, 0.3, 1] }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/**
 * Immediate (on-load) fade/slide — used for hero overline, subtitle, CTA group.
 */
export function FadeIn({ children, delay = 0, y = 18, duration = 0.65, className, ...rest }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
